import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, 
  BarChart3, 
  TrendingUp, 
  FileText, 
  ClipboardList, 
  Users, 
  Receipt as ReceiptIcon, 
  Calendar, 
  Boxes, 
  Building, 
  Wallet, 
  Stethoscope, 
  Syringe, 
  FileArchive, 
  Building2, 
  PenTool, 
  ShieldCheck, 
  Settings, 
  LogOut, 
  Calculator, 
  Plus, 
  ChevronRight, 
  ChevronDown, 
  Menu, 
  X, 
  HelpCircle,
  Database,
  ArrowUpRight,
  Shield,
  Layers,
  Crown,
  Dog,
  Wrench,
  Scissors,
  Zap,
  DollarSign,
  CreditCard,
  ArrowLeft
} from 'lucide-react';
import { AgenticCommandModal } from '../../components/AgenticCommandModal';
import { UserProfile, FinancialConfig, AppView } from '../../../../types';
import { 
  EnterpriseVerticalId, 
  ENTERPRISE_VERTICALS, 
  detectVerticalFromNiche 
} from '../config/enterpriseVerticals';
import { isMasterEmail } from '../../../../constants';
import { resolveCompanyName } from '../../../../hooks/useCompanyProfile';

export interface EnterpriseSuiteLayoutProps {
  user: UserProfile;
  config: FinancialConfig | null;
  currentView: AppView;
  vertical?: EnterpriseVerticalId;
  onSelectVertical?: (vertical: EnterpriseVerticalId) => void;
  onNavigate: (view: AppView) => void;
  onQuickBudget: () => void;
  onLogout: () => void;
  onOpenHelp: () => void;
  onOpenTeamModal: () => void;
  onOpenProfile: () => void;
  onNewQuote?: () => void;
  onNewWorkOrder?: () => void;
  onNewBatch?: () => void;
  onNewClient?: () => void;
  badges?: {
    quotesCount?: number;
    openWorkOrdersCount?: number;
    expiringBatchesCount?: number;
    overdueVaccinesCount?: number;
  };
  children: React.ReactNode;
}

export default function EnterpriseSuiteLayout({
  user,
  config,
  currentView,
  vertical,
  onSelectVertical,
  onNavigate,
  onQuickBudget,
  onLogout,
  onOpenHelp,
  onOpenTeamModal,
  onOpenProfile,
  onNewQuote,
  onNewWorkOrder,
  onNewBatch,
  onNewClient,
  badges = {},
  children
}: EnterpriseSuiteLayoutProps) {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isNewActionMenuOpen, setIsNewActionMenuOpen] = useState(false);
  const [isAgenticModalOpen, setIsAgenticModalOpen] = useState(false);

  // Listener universal para atalho Ctrl+K / Cmd+K (Modo Comando Agêntico)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsAgenticModalOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Vertical ativa com fallback automático inteligente
  const activeVertical: EnterpriseVerticalId = vertical || detectVerticalFromNiche(user.niche, user.profession);
  const currentPreset = ENTERPRISE_VERTICALS[activeVertical] || ENTERPRISE_VERTICALS.general_corporate;
  const VerticalBadgeIcon = currentPreset.badgeIcon;

  // Fechar sidebar mobile ao redimensionar tela
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = () => {
      setIsNewActionMenuOpen(false);
    };
    if (isNewActionMenuOpen) {
      window.addEventListener('click', handleClickOutside);
      return () => window.removeEventListener('click', handleClickOutside);
    }
  }, [isNewActionMenuOpen]);

  const companyName = resolveCompanyName(user, 'Minha Empresa');
  const companyCNPJ = user.document || 'Matriz Corporativa';

  // Identificação do título e breadcrumb atual em linguagem simples
  const getViewInfo = (view: AppView) => {
    switch (view) {
      case 'erp-hub': return { dept: 'Início', title: 'Painel da Empresa' };
      case 'calendar': return { dept: 'Agenda', title: currentPreset.terminology.agendaLabel || 'Agenda' };
      case 'clients': return { dept: 'Clientes e pets', title: 'Clientes' };
      case 'prontuario_soap': return { dept: 'Clientes e pets', title: activeVertical === 'pet_vet' ? 'Fichas dos pets' : 'Fichas e prontuários' };
      case 'retorno_vacinas': return { dept: 'Clientes e pets', title: 'Vacinas e vermífugos' };
      case 'cofre_exames': return { dept: 'Clientes e pets', title: 'Exames e imagens' };
      case 'recepcao_pdv': return { dept: 'Vendas', title: 'Balcão e caixa' };
      case 'speed_pos': return { dept: 'Vendas', title: 'Balcão da sorveteria' };
      case 'history': return { dept: 'Vendas', title: 'Orçamentos' };
      case 'pipeline': return { dept: 'Vendas', title: 'Orçamentos em funil' };
      case 'chat': return { dept: 'Vendas', title: 'Novo Orçamento' };
      case 'work-orders': return { dept: 'Vendas', title: activeVertical === 'technical_service' ? 'Ordens de Serviço & Aparelhos' : 'Ordens de Serviço' };
      case 'receipts': return { dept: 'Vendas', title: 'Recibos' };
      case 'signatures': return { dept: 'Vendas', title: 'Assinaturas' };
      case 'financial_hub': return { dept: 'Contas', title: 'Contas a pagar e receber' };
      case 'executive-cockpit': return { dept: 'Contas', title: 'Lucro e meta' };
      case 'portal_contador': return { dept: 'Contas', title: 'Contador' };
      case 'costs': return { dept: 'Custos e estoque', title: 'Custos e insumos' };
      case 'estoque_hub': return { dept: 'Custos e estoque', title: 'Estoque' };
      case 'suppliers': return { dept: 'Custos e estoque', title: 'Fornecedores' };
      case 'market-radar': return { dept: 'Custos e estoque', title: 'Preços da região' };
      case 'industria': return { dept: 'Custos e estoque', title: 'Indústria & PCP' };
      case 'profile': return { dept: 'Configurações', title: 'Configurações' };
      default: return { dept: 'Início', title: 'Painel da Empresa' };
    }
  };

  const currentInfo = getViewInfo(currentView);

  // Estados dos menus sanfona: submenu exclusivo apenas no item ativo
  const isClientesActive = ['clients', 'prontuario_soap', 'retorno_vacinas', 'cofre_exames'].includes(currentView);
  const isVendasActive = ['history', 'pipeline', 'chat', 'work-orders', 'receipts', 'signatures', 'recepcao_pdv', 'speed_pos'].includes(currentView);
  const isContasActive = ['financial_hub', 'executive-cockpit', 'portal_contador'].includes(currentView);
  const isCustosActive = ['costs', 'suppliers', 'estoque_hub', 'market-radar', 'industria'].includes(currentView);

  const [openClientes, setOpenClientes] = useState(isClientesActive);
  const [openVendas, setOpenVendas] = useState(isVendasActive);
  const [openContas, setOpenContas] = useState(isContasActive);
  const [openCustos, setOpenCustos] = useState(isCustosActive);

  useEffect(() => {
    // Regra UX: Submenu apenas no item aberto
    setOpenClientes(isClientesActive);
    setOpenVendas(isVendasActive);
    setOpenContas(isContasActive);
    setOpenCustos(isCustosActive);
  }, [currentView, isClientesActive, isVendasActive, isContasActive, isCustosActive]);

  const handleItemClick = (targetId: AppView) => {
    if (targetId === ('equipe' as any)) {
      onOpenTeamModal();
    } else {
      onNavigate(targetId);
    }
    setIsMobileSidebarOpen(false);
  };

  // Se a tela for Configurações, renderiza limpa sem barra lateral para foco total
  if (currentView === 'profile') {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col font-sans text-zinc-900 antialiased selection:bg-blue-100 selection:text-blue-900">
        <header className="sticky top-0 z-30 bg-white border-b border-zinc-200/90 h-16 px-6 flex items-center justify-between shadow-2xs">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onNavigate('erp-hub')}
              className="p-2 text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100 rounded-xl cursor-pointer flex items-center gap-2 font-bold text-sm transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Voltar para o Início</span>
            </button>
            <div className="h-5 w-px bg-zinc-200" />
            <span className="text-sm font-black text-zinc-900">Configurações</span>
          </div>
          <div className="text-xs text-zinc-500 font-bold hidden sm:block">
            Passo a passo de configuração
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 max-w-3xl w-full mx-auto">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col font-sans text-zinc-900 antialiased selection:bg-blue-100 selection:text-blue-900">
      
      {/* ─── BARRA LATERAL (SIDEBAR) DEDICADA EM TEMA CLARO ─── */}
      <div className="flex flex-1 relative overflow-hidden">
        
        {/* Backdrop para mobile */}
        {isMobileSidebarOpen && (
          <div 
            className="fixed inset-0 bg-neutral-900/40 backdrop-blur-xs z-40 lg:hidden"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        {/* Sidebar Container */}
        <aside className={`
          fixed lg:static top-0 bottom-0 left-0 z-50
          w-72 bg-white border-r border-zinc-200/90
          flex flex-col shrink-0
          transition-transform duration-300 ease-in-out
          ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          shadow-xs lg:shadow-none
        `}>
          {/* Header da Sidebar: Identidade da Empresa e Nicho (Apenas UMA vez) */}
          <div className="p-4 border-b border-zinc-100 bg-white">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2.5 min-w-0">
                {user.logoUrl ? (
                  <img 
                    src={user.logoUrl} 
                    alt={companyName}
                    className="w-10 h-10 rounded-xl object-contain border border-zinc-200 bg-white p-0.5 shadow-xs shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-black text-sm shadow-xs shrink-0">
                    {companyName.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <h2 className="text-sm font-black text-zinc-900 truncate leading-tight">
                    {companyName}
                  </h2>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[11px] font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded-md border border-blue-200/60 truncate">
                      {currentPreset.name}
                    </span>
                  </div>
                </div>
              </div>

              {/* Botão de fechar em mobile */}
              <button 
                type="button"
                onClick={() => setIsMobileSidebarOpen(false)}
                className="lg:hidden p-1.5 text-zinc-400 hover:text-zinc-700 rounded-lg hover:bg-zinc-100 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* CNPJ / Documento discreto */}
            <div className="text-[10px] font-medium text-zinc-400 px-0.5 truncate">
              {companyCNPJ}
            </div>

            {/* Modo Demonstração exclusivo para Administradores Master */}
            {isMasterEmail(user?.email) && (
              <div className="mt-2.5 bg-amber-500/10 border border-amber-500/30 rounded-xl p-1.5">
                <div className="text-[9px] font-black uppercase tracking-wider text-amber-800 mb-1 flex items-center justify-between">
                  <span>Modo Demonstração (Admin)</span>
                  <span className="text-[8px] font-bold text-amber-700 bg-amber-200/60 px-1 py-0.2 rounded">Master</span>
                </div>
                <div className="grid grid-cols-6 gap-1 p-1 bg-white rounded-lg border border-amber-200/80">
                  {(['pet_vet', 'medical', 'technical_service', 'food_icecream', 'retail_bulk', 'fashion_retail'] as EnterpriseVerticalId[]).map((vId) => {
                    const preset = ENTERPRISE_VERTICALS[vId];
                    const Icon = preset.badgeIcon;
                    const isSelected = activeVertical === vId;
                    const shortLabel = vId === 'pet_vet' ? 'Pet' : vId === 'medical' ? 'Médico' : vId === 'technical_service' ? 'Oficina' : vId === 'food_icecream' ? 'Sorvete' : vId === 'retail_bulk' ? 'Granel' : 'Moda';

                    return (
                      <button
                        key={vId}
                        type="button"
                        onClick={() => onSelectVertical?.(vId)}
                        className={`py-1 px-0.5 rounded text-[9px] font-black flex flex-col items-center justify-center gap-0.5 transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-blue-600 text-white shadow-xs'
                            : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100'
                        }`}
                        title={preset.name}
                      >
                        <Icon className="w-3 h-3 shrink-0" />
                        <span className="text-[8px] truncate leading-none">{shortLabel}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ─── NAVEGAÇÃO UNIFICADA EM 7 ITENS PRINCIPAIS (UX CHECKLIST) ─── */}
          <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5 scrollbar-thin scrollbar-thumb-zinc-200 text-sm">
            
            {/* 1. Início */}
            <button
              type="button"
              onClick={() => handleItemClick('erp-hub')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-bold transition-all text-left cursor-pointer ${
                currentView === 'erp-hub'
                  ? 'bg-blue-600 text-white shadow-xs font-black'
                  : 'text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100'
              }`}
            >
              <LayoutDashboard className={`w-4 h-4 shrink-0 ${currentView === 'erp-hub' ? 'text-white' : 'text-zinc-500'}`} />
              <span className="truncate">Início</span>
            </button>

            {/* 2. Agenda */}
            <button
              type="button"
              onClick={() => handleItemClick('calendar')}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-[13px] font-bold transition-all text-left cursor-pointer ${
                currentView === 'calendar'
                  ? 'bg-blue-600 text-white shadow-xs font-black'
                  : 'text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100'
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <Calendar className={`w-4 h-4 shrink-0 ${currentView === 'calendar' ? 'text-white' : 'text-zinc-500'}`} />
                <span className="truncate">Agenda</span>
              </div>
            </button>

            {/* 3. Clientes e pets (Sanfona) */}
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => setOpenClientes(prev => !prev)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-[13px] font-bold transition-all text-left cursor-pointer ${
                  isClientesActive && !openClientes
                    ? 'bg-blue-50 text-blue-700 font-black'
                    : 'text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Users className={`w-4 h-4 shrink-0 ${isClientesActive ? 'text-blue-600' : 'text-zinc-500'}`} />
                  <span className="truncate">{activeVertical === 'pet_vet' ? 'Clientes e pets' : (currentPreset.terminology.clientPluralLabel || 'Clientes')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {badges.overdueVaccinesCount ? (
                    <span className="text-[9px] font-bold px-1.5 py-0.2 rounded-full bg-purple-100 text-purple-900">
                      {badges.overdueVaccinesCount}
                    </span>
                  ) : null}
                  <ChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${openClientes ? 'rotate-180' : ''}`} />
                </div>
              </button>

              {openClientes && (
                <div className="pl-6 pr-1 space-y-1 pt-0.5 border-l-2 border-zinc-200 ml-4">
                  <button
                    type="button"
                    onClick={() => handleItemClick('clients')}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left cursor-pointer ${
                      currentView === 'clients'
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="truncate">Clientes</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleItemClick('prontuario_soap')}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left cursor-pointer ${
                      currentView === 'prontuario_soap'
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="truncate">{activeVertical === 'pet_vet' ? 'Fichas dos pets' : 'Fichas e prontuários'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleItemClick('retorno_vacinas')}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left cursor-pointer ${
                      currentView === 'retorno_vacinas'
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="truncate">Vacinas</span>
                    {badges.overdueVaccinesCount ? (
                      <span className="text-[9px] font-bold px-1.5 rounded-full bg-purple-100 text-purple-900">
                        {badges.overdueVaccinesCount}
                      </span>
                    ) : null}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleItemClick('cofre_exames')}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left cursor-pointer ${
                      currentView === 'cofre_exames'
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="truncate">Exames</span>
                  </button>
                </div>
              )}
            </div>

            {/* 4. Vendas (Sanfona) */}
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => setOpenVendas(prev => !prev)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-[13px] font-bold transition-all text-left cursor-pointer ${
                  isVendasActive && !openVendas
                    ? 'bg-blue-50 text-blue-700 font-black'
                    : 'text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className={`w-4 h-4 shrink-0 ${isVendasActive ? 'text-blue-600' : 'text-zinc-500'}`} />
                  <span className="truncate">Vendas</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {(badges.quotesCount || badges.openWorkOrdersCount) ? (
                    <span className="text-[9px] font-bold px-1.5 py-0.2 rounded-full bg-blue-100 text-blue-900">
                      {(badges.quotesCount || 0) + (badges.openWorkOrdersCount || 0)}
                    </span>
                  ) : null}
                  <ChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${openVendas ? 'rotate-180' : ''}`} />
                </div>
              </button>

              {openVendas && (
                <div className="pl-6 pr-1 space-y-1 pt-0.5 border-l-2 border-zinc-200 ml-4">
                  <button
                    type="button"
                    onClick={() => handleItemClick(activeVertical === 'food_icecream' ? 'speed_pos' : 'recepcao_pdv')}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left cursor-pointer ${
                      currentView === 'recepcao_pdv' || currentView === 'speed_pos'
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="truncate">Balcão e caixa</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleItemClick('history')}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left cursor-pointer ${
                      currentView === 'history' || currentView === 'chat' || currentView === 'pipeline'
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="truncate">Orçamentos</span>
                    {badges.quotesCount ? (
                      <span className="text-[9px] font-bold px-1.5 rounded-full bg-zinc-100 text-zinc-700">
                        {badges.quotesCount}
                      </span>
                    ) : null}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleItemClick('work-orders')}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left cursor-pointer ${
                      currentView === 'work-orders'
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="truncate">Ordens de serviço</span>
                    {badges.openWorkOrdersCount ? (
                      <span className="text-[9px] font-bold px-1.5 rounded-full bg-amber-100 text-amber-900">
                        {badges.openWorkOrdersCount}
                      </span>
                    ) : null}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleItemClick('receipts')}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left cursor-pointer ${
                      currentView === 'receipts'
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="truncate">Recibos</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleItemClick('signatures')}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left cursor-pointer ${
                      currentView === 'signatures'
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="truncate">Assinaturas</span>
                  </button>
                </div>
              )}
            </div>

            {/* 5. Contas (Sanfona) */}
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => setOpenContas(prev => !prev)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-[13px] font-bold transition-all text-left cursor-pointer ${
                  isContasActive && !openContas
                    ? 'bg-blue-50 text-blue-700 font-black'
                    : 'text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Wallet className={`w-4 h-4 shrink-0 ${isContasActive ? 'text-blue-600' : 'text-zinc-500'}`} />
                  <span className="truncate">Contas</span>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${openContas ? 'rotate-180' : ''}`} />
              </button>

              {openContas && (
                <div className="pl-6 pr-1 space-y-1 pt-0.5 border-l-2 border-zinc-200 ml-4">
                  <button
                    type="button"
                    onClick={() => handleItemClick('financial_hub')}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left cursor-pointer ${
                      currentView === 'financial_hub'
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="truncate">Contas a pagar e receber</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleItemClick('executive-cockpit')}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left cursor-pointer ${
                      currentView === 'executive-cockpit'
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="truncate">Lucro e meta</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleItemClick('portal_contador')}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left cursor-pointer ${
                      currentView === 'portal_contador'
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="truncate">Contador</span>
                  </button>
                </div>
              )}
            </div>

            {/* 6. Custos e estoque (Sanfona) */}
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => setOpenCustos(prev => !prev)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-[13px] font-bold transition-all text-left cursor-pointer ${
                  isCustosActive && !openCustos
                    ? 'bg-blue-50 text-blue-700 font-black'
                    : 'text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Boxes className={`w-4 h-4 shrink-0 ${isCustosActive ? 'text-blue-600' : 'text-zinc-500'}`} />
                  <span className="truncate">Custos e estoque</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {badges.expiringBatchesCount ? (
                    <span className="text-[9px] font-bold px-1.5 py-0.2 rounded-full bg-amber-100 text-amber-900">
                      {badges.expiringBatchesCount}
                    </span>
                  ) : null}
                  <ChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${openCustos ? 'rotate-180' : ''}`} />
                </div>
              </button>

              {openCustos && (
                <div className="pl-6 pr-1 space-y-1 pt-0.5 border-l-2 border-zinc-200 ml-4">
                  <button
                    type="button"
                    onClick={() => handleItemClick('costs')}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left cursor-pointer ${
                      currentView === 'costs'
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="truncate">Custos e insumos</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleItemClick('estoque_hub')}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left cursor-pointer ${
                      currentView === 'estoque_hub'
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="truncate">Estoque</span>
                    {badges.expiringBatchesCount ? (
                      <span className="text-[9px] font-bold px-1.5 rounded-full bg-amber-100 text-amber-900">
                        {badges.expiringBatchesCount} venc.
                      </span>
                    ) : null}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleItemClick('suppliers')}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left cursor-pointer ${
                      currentView === 'suppliers'
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="truncate">Fornecedores</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleItemClick('market-radar')}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left cursor-pointer ${
                      currentView === 'market-radar'
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="truncate">Preços da região</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleItemClick('industria')}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left cursor-pointer ${
                      currentView === 'industria'
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="truncate">Indústria & PCP</span>
                  </button>
                </div>
              )}
            </div>

            {/* 7. Configurações (Item Direto sem Submenu) */}
            <button
              type="button"
              onClick={() => handleItemClick('profile')}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-[13px] font-bold transition-all text-left cursor-pointer text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Settings className="w-4 h-4 shrink-0 text-zinc-500" />
                <span className="truncate">Configurações</span>
              </div>
            </button>

          </nav>

          {/* Rodapé da Sidebar: Usuário + Alternância */}
          <div className="p-3 border-t border-zinc-100 bg-white space-y-2">
            {/* Atalho para a Calculadora Rápida */}
            <button
              type="button"
              onClick={onQuickBudget}
              className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl text-[11px] font-bold text-zinc-600 hover:text-blue-700 hover:bg-blue-50/60 border border-transparent hover:border-blue-200/60 transition-all cursor-pointer"
              title="Abrir a calculadora rápida de orçamentos e preços"
            >
              <div className="flex items-center gap-2">
                <Calculator className="w-3.5 h-3.5 text-blue-600" />
                <span>Calculadora Rápida de Preços</span>
              </div>
              <ArrowUpRight className="w-3 h-3 text-zinc-400" />
            </button>

            {/* Atalho para o Modo Comando Agêntico (⚡) */}
            <button
              type="button"
              onClick={() => setIsAgenticModalOpen(true)}
              className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl text-[11px] font-bold text-amber-800 hover:text-amber-900 bg-amber-50/70 hover:bg-amber-100/80 border border-amber-200/70 transition-all cursor-pointer"
              title="Abrir Modo Comando Agêntico (Ctrl + K)"
            >
              <div className="flex items-center gap-2">
                <Zap className="w-3.5 h-3.5 text-amber-600 fill-current" />
                <span>Modo Comando</span>
              </div>
              <kbd className="text-[9px] bg-amber-200/60 text-amber-900 px-1 py-0.5 rounded font-mono font-bold">⌘K</kbd>
            </button>

            {/* Informações do Usuário & Logout */}
            <div className="pt-2 border-t border-zinc-100 flex items-center justify-between">
              <button
                type="button"
                onClick={onOpenProfile}
                className="flex items-center gap-2 min-w-0 text-left hover:opacity-80 transition-opacity cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-zinc-100 text-zinc-700 border border-zinc-200 flex items-center justify-center text-[10px] font-black shrink-0">
                  {(user.ownerName || 'US').slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <span className="text-[11px] font-bold text-zinc-900 block truncate">
                    {user.ownerName || 'Proprietário'}
                  </span>
                  <span className="text-[9px] font-medium text-zinc-400 block truncate">
                    Proprietário / Administrador
                  </span>
                </div>
              </button>

              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={onOpenHelp}
                  className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
                  title="Ajuda e Suporte"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={onLogout}
                  className="p-1.5 text-zinc-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                  title="Encerrar Sessão"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </aside>

        {/* ─── ÁREA DE CONTEÚDO PRINCIPAL (WIDESCREEN FLUIDO) ─── */}
        <div className="flex-1 flex flex-col min-w-0 overflow-y-auto">
          
          {/* Topbar Executiva (H-14, 56px) */}
          <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-zinc-200/90 h-14 px-4 sm:px-6 flex items-center justify-between shadow-2xs">
            
            {/* Lado Esquerdo: Mobile Menu + Breadcrumb Corporativo */}
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => setIsMobileSidebarOpen(true)}
                className="lg:hidden p-2 -ml-2 text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100 rounded-xl cursor-pointer"
                aria-label="Abrir Menu"
              >
                <Menu className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-2 text-xs font-semibold text-zinc-500 min-w-0">
                <span className="hidden sm:inline font-medium text-zinc-400">
                  {companyName}
                </span>
                <span className="hidden sm:inline text-zinc-300">/</span>
                <span className="text-zinc-400 hidden md:inline truncate">
                  {currentInfo.dept}
                </span>
                <span className="text-zinc-300 hidden md:inline">/</span>
                <span className="font-bold text-zinc-900 truncate">
                  {currentInfo.title}
                </span>
              </div>
            </div>

            {/* Lado Direito: Vertical Ativa + Sincronização + Botão Universal "+ Nova Ação" */}
            <div className="flex items-center gap-3">
              
              {/* Indicador de Segurança dos Dados */}
              <div 
                className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200/70 text-emerald-800 text-[10px] font-bold"
                title="Seus dados estão protegidos e salvos na nuvem"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span>Dados Salvos com Segurança</span>
              </div>

              {/* Botão de Comando Rápido Agêntico (⚡) */}
              <button
                type="button"
                onClick={() => setIsAgenticModalOpen(true)}
                className="px-2.5 py-1.5 rounded-xl flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 active:scale-95 text-white font-black text-xs shadow-xs cursor-pointer transition-all"
                title="Abrir Modo Comando Agêntico (Ctrl + K)"
              >
                <Zap className="w-3.5 h-3.5 fill-current" />
                <span className="hidden sm:inline">Modo Comando</span>
                <kbd className="hidden sm:inline text-[9px] bg-amber-700/60 px-1 py-0.5 rounded font-mono">⌘K</kbd>
              </button>

              {/* Dropdown Universal "+ Nova Ação" contextual por vertical */}
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  onClick={() => setIsNewActionMenuOpen(!isNewActionMenuOpen)}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-black text-xs rounded-xl shadow-xs flex items-center gap-1.5 transition-all cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  <span>Novo</span>
                  <ChevronDown className="w-3 h-3 opacity-80" />
                </button>

                {isNewActionMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white border border-zinc-200 rounded-2xl shadow-xl py-1.5 z-50 animate-in fade-in-50 zoom-in-95 duration-100">
                    <div className="px-3 py-1 text-[9px] font-black uppercase tracking-wider text-zinc-400">
                      Ações Rápidas: {currentPreset.badgeLabel}
                    </div>

                    {activeVertical === 'pet_vet' && (
                      <>
                        <button
                          type="button"
                          onClick={() => { setIsNewActionMenuOpen(false); onNavigate('calendar'); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-zinc-700 hover:bg-zinc-50 hover:text-blue-700 transition-colors text-left cursor-pointer"
                        >
                          <Calendar className="w-4 h-4 text-blue-600" />
                          <span>Agendar Horário (Consulta ou Banho)</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => { setIsNewActionMenuOpen(false); onNavigate('prontuario_soap'); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-zinc-700 hover:bg-zinc-50 hover:text-emerald-700 transition-colors text-left cursor-pointer"
                        >
                          <Stethoscope className="w-4 h-4 text-emerald-600" />
                          <span>Nova Consulta com Veterinário</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => { setIsNewActionMenuOpen(false); onNavigate('retorno_vacinas'); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-zinc-700 hover:bg-zinc-50 hover:text-purple-700 transition-colors text-left cursor-pointer"
                        >
                          <Syringe className="w-4 h-4 text-purple-600" />
                          <span>Lembrete de Vacina no WhatsApp</span>
                        </button>
                      </>
                    )}

                    {activeVertical === 'medical' && (
                      <>
                        <button
                          type="button"
                          onClick={() => { setIsNewActionMenuOpen(false); onNavigate('calendar'); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-zinc-700 hover:bg-zinc-50 hover:text-blue-700 transition-colors text-left cursor-pointer"
                        >
                          <Calendar className="w-4 h-4 text-blue-600" />
                          <span>Agendar Consulta Médica</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => { setIsNewActionMenuOpen(false); onNavigate('prontuario_soap'); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-zinc-700 hover:bg-zinc-50 hover:text-emerald-700 transition-colors text-left cursor-pointer"
                        >
                          <Stethoscope className="w-4 h-4 text-emerald-600" />
                          <span>Abrir Ficha do Paciente</span>
                        </button>
                      </>
                    )}

                    {activeVertical === 'technical_service' && (
                      <>
                        <button
                          type="button"
                          onClick={() => { setIsNewActionMenuOpen(false); onNewWorkOrder?.(); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-zinc-700 hover:bg-zinc-50 hover:text-amber-700 transition-colors text-left cursor-pointer"
                        >
                          <ClipboardList className="w-4 h-4 text-amber-600" />
                          <span>Nova Ordem de Serviço (OS)</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => { setIsNewActionMenuOpen(false); onNavigate('calendar'); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-zinc-700 hover:bg-zinc-50 hover:text-purple-700 transition-colors text-left cursor-pointer"
                        >
                          <Calendar className="w-4 h-4 text-purple-600" />
                          <span>Agendar Vistoria de Seguro</span>
                        </button>
                      </>
                    )}

                    {/* Ações universais adaptadas */}
                    {onNewQuote && (
                      <button
                        type="button"
                        onClick={() => { setIsNewActionMenuOpen(false); onNewQuote(); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-zinc-700 hover:bg-zinc-50 hover:text-blue-700 transition-colors text-left cursor-pointer"
                      >
                        <FileText className="w-4 h-4 text-blue-600" />
                        <span>Novo Orçamento & Proposta</span>
                      </button>
                    )}
                    {onNewBatch && (
                      <button
                        type="button"
                        onClick={() => { setIsNewActionMenuOpen(false); onNewBatch(); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-zinc-700 hover:bg-zinc-50 hover:text-indigo-700 transition-colors text-left cursor-pointer"
                      >
                        <Boxes className="w-4 h-4 text-indigo-600" />
                        <span>Nova Entrada de Estoque / Lote</span>
                      </button>
                    )}
                    {onNewClient && (
                      <button
                        type="button"
                        onClick={() => { setIsNewActionMenuOpen(false); onNewClient(); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-zinc-700 hover:bg-zinc-50 hover:text-emerald-700 transition-colors text-left cursor-pointer"
                      >
                        <Users className="w-4 h-4 text-emerald-600" />
                        <span>Cadastrar {currentPreset.terminology.clientLabel}</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Área Widescreen Dinâmica dos Módulos */}
          <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-7xl w-full mx-auto">
            {children}
          </main>
        </div>
      </div>

      {/* Modal de Comando Rápido Agêntico (Text-to-Action) */}
      <AgenticCommandModal
        isOpen={isAgenticModalOpen}
        onClose={() => setIsAgenticModalOpen(false)}
        userTier={
          user.isMasterUser || ['Empresa', 'Empresarial', 'Enterprise', 'empresa', 'enterprise'].includes(user.plan || '')
            ? 'empresa'
            : user.plan === 'Especialista' || (user.plan as string) === 'Premium' || user.isTrialActive
            ? 'especialista'
            : user.plan === 'Pro'
            ? 'pro'
            : 'free'
        }
        onNavigate={onNavigate}
        costsViewId="costs"
      />
    </div>
  );
}
